import React, { useRef, useState, useEffect, useLayoutEffect, useMemo } from 'react';
import { Menu } from 'primereact/menu';
import { Button } from 'primereact/button';
import { Paginator } from 'primereact/paginator';

const ArticleRewardListing = ({
    data, onEdit, onDelete,
    onSendSubmit, onCancelSubmit,
    onBulkSend, onBulkCancel, onBulkDelete,
    isLoading, toast, searchQuery,
}) => {
    const menu = useRef(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [selectedIds, setSelectedIds] = useState([]);

    const [first, setFirst] = useState(0);
    const rows = 20;

    const containerRef = useRef(null);
    const scrollLeftRef = useRef(0);

    const handleScroll = (e) => {
        scrollLeftRef.current = e.target.scrollLeft;
    };

    useLayoutEffect(() => {
        if (containerRef.current && scrollLeftRef.current > 0) {
            containerRef.current.scrollLeft = scrollLeftRef.current;
        }
    }, [data, first]);

    useEffect(() => {
        setFirst(0);
        setSelectedIds([]);
    }, [searchQuery]);

    useEffect(() => {
        setSelectedIds(prev => prev.filter(id => data.some(d => d.Id === id)));

        if (data.length > 0 && first >= data.length) {
            setFirst(Math.max(0, Math.floor((data.length - 1) / rows) * rows));
        } else if (data.length === 0) {
            setFirst(0);
        }
    }, [data, first, rows]);

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedIds(data.map(item => item.Id));
        } else {
            setSelectedIds([]);
        }
    };

    const handleSelectRow = (e, id) => {
        if (e.target.checked) {
            setSelectedIds(prev => [...prev, id]);
        } else {
            setSelectedIds(prev => prev.filter(itemId => itemId !== id));
        }
    };

    const paginatedData = useMemo(() => data.slice(first, first + rows), [data, first, rows]);

    const handleDownload = (attachmentData) => {
        if (!attachmentData) {
            if (toast && toast.current) toast.current.show({ severity: 'info', summary: 'Thông báo', detail: 'Đăng ký này chưa có tệp đính kèm!', life: 3000 });
            else alert("Đăng ký này chưa có tệp đính kèm!");
            return;
        }
        const triggerDownload = (name, data) => {
            const link = document.createElement('a');
            link.href = data;
            link.download = name;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        };
        try {
            if (attachmentData.startsWith('[')) {
                const files = JSON.parse(attachmentData);
                if (files.length === 0) {
                    if (toast && toast.current) toast.current.show({ severity: 'info', summary: 'Thông báo', detail: 'Đăng ký này chưa có tệp đính kèm!', life: 3000 });
                    else alert("Đăng ký này chưa có tệp đính kèm!");
                    return;
                }
                files.forEach(f => triggerDownload(f.name, f.data));
                if (files.length > 1) {
                    if (toast && toast.current) toast.current.show({ severity: 'success', summary: 'Thành công', detail: `Đang tải xuống ${files.length} tệp minh chứng...`, life: 3000 });
                }
            } else if (attachmentData.includes('|')) {
                const parts = attachmentData.split('|');
                triggerDownload(parts[0], parts[1]);
            } else {
                if (toast && toast.current) toast.current.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Định dạng tệp minh chứng không hợp lệ!', life: 3000 });
                else alert("Định dạng tệp minh chứng không hợp lệ!");
            }
        } catch (error) {
            console.error("Lỗi tải tệp minh chứng:", error);
            if (toast && toast.current) toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Đã xảy ra lỗi khi đọc dữ liệu tệp!', life: 3000 });
            else alert("Đã xảy ra lỗi khi đọc dữ liệu tệp!");
        }
    };

    const getStatusStyle = (status) => {
        switch (status) {
            case 'Đã duyệt': return { backgroundColor: '#27ae60', color: '#fff' };
            case 'Đã gửi duyệt': return { backgroundColor: '#3498db', color: '#fff' };
            case 'Từ chối': return { backgroundColor: '#e74c3c', color: '#fff' };
            case 'Yêu cầu chỉnh sửa': return { backgroundColor: '#9b59b6', color: '#fff' };
            case 'Lưu nháp': return { backgroundColor: '#f39c12', color: '#fff' };
            default: return { backgroundColor: '#95a5a6', color: '#fff' };
        }
    };

    const itemTemplate = (item, options) => {
        return (
            <div className={options.className} onClick={(e) => {
                if (e.originalEvent) e.originalEvent.preventDefault();
                options.onClick(e);
            }}
                style={{
                    display: 'flex', alignItems: 'center', padding: '10px 15px',
                    pointerEvents: item.disabled ? 'none' : 'auto',
                    opacity: item.disabled ? 0.5 : 1,
                    transition: 'background-color 0.2s',
                    cursor: item.disabled ? 'default' : 'pointer'
                }}>
                <i className={item.icon} style={{ color: item.color, width: '24px', fontSize: '1.1rem', marginRight: '8px' }}></i>
                <span style={{ fontSize: '14px', fontWeight: 500, color: '#495057' }}>{item.label}</span>
            </div>
        );
    };

    const getMenuItems = () => {
        if (!selectedItem) return [];

        const isLocked = selectedItem.Status === 'Đã gửi duyệt' || selectedItem.Status === 'Đã duyệt';
        const items = [];

        if (selectedItem.Status === 'Lưu nháp' || selectedItem.Status === 'Từ chối' || selectedItem.Status === 'Yêu cầu chỉnh sửa') {
            items.push({ label: 'Gửi duyệt', icon: 'fa-solid fa-paper-plane', color: '#3498db', command: () => onSendSubmit(selectedItem.Id), template: itemTemplate });
        }
        else if (selectedItem.Status === 'Đã gửi duyệt') {
            items.push({ label: 'Hủy gửi duyệt', icon: 'fa-solid fa-rotate-left', color: '#f39c12', command: () => onCancelSubmit(selectedItem.Id), template: itemTemplate });
        }

        items.push({ separator: true });

        items.push({ label: 'Chỉnh sửa', icon: 'fa-solid fa-pen-to-square', color: '#3498db', disabled: isLocked, command: () => onEdit(selectedItem), template: itemTemplate });
        items.push({ label: 'Xóa', icon: 'fa-solid fa-trash-can', color: '#e74c3c', disabled: isLocked, command: () => onDelete(selectedItem.Id), template: itemTemplate });

        return items;
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(amount || 0);
    };

    const onPageChange = (event) => {
        setFirst(event.first);
    };

    return (
        <>
            {selectedIds.length > 0 && (
                <div style={{
                    padding: '12px 16px',
                    background: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    marginBottom: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: '12px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ background: '#e0f2fe', padding: '8px', borderRadius: '50%', display: 'flex', width: '36px', height: '36px', justifyContent: 'center', alignItems: 'center' }}>
                            <i className="fa-solid fa-list-check" style={{ color: '#0284c7', fontSize: '16px' }}></i>
                        </div>
                        <span style={{ fontSize: '15px', color: '#475569' }}>
                            Đã chọn <strong style={{ color: '#0f172a', fontSize: '18px', margin: '0 4px' }}>{selectedIds.length}</strong> mục
                        </span>
                    </div>

                    <div style={{
                        display: 'flex',
                        gap: '8px',
                        flexWrap: 'wrap'
                    }}>
                        <button
                            onClick={() => onBulkSend(selectedIds)}
                            style={{
                                backgroundColor: '#3b82f6', color: 'white', border: 'none',
                                padding: '8px 12px', borderRadius: '6px', fontSize: '13px',
                                fontWeight: '500', cursor: 'pointer', display: 'flex',
                                alignItems: 'center', gap: '6px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#2563eb'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                            onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#3b82f6'; e.currentTarget.style.transform = 'translateY(0)'; }}
                        >
                            <i className="fa-solid fa-paper-plane" style={{ fontSize: '12px' }}></i> Gửi duyệt
                        </button>

                        <button
                            onClick={() => onBulkCancel(selectedIds)}
                            style={{
                                backgroundColor: '#f59e0b', color: 'white', border: 'none',
                                padding: '8px 12px', borderRadius: '6px', fontSize: '13px',
                                fontWeight: '500', cursor: 'pointer', display: 'flex',
                                alignItems: 'center', gap: '6px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#d97706'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                            onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#f59e0b'; e.currentTarget.style.transform = 'translateY(0)'; }}
                        >
                            <i className="fa-solid fa-rotate-left" style={{ fontSize: '12px' }}></i> Hủy
                        </button>

                        <button
                            onClick={() => onBulkDelete(selectedIds)}
                            style={{
                                backgroundColor: '#ef4444', color: 'white', border: 'none',
                                padding: '8px 12px', borderRadius: '6px', fontSize: '13px',
                                fontWeight: '500', cursor: 'pointer', display: 'flex',
                                alignItems: 'center', gap: '6px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#dc2626'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                            onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#ef4444'; e.currentTarget.style.transform = 'translateY(0)'; }}
                        >
                            <i className="fa-solid fa-trash-can" style={{ fontSize: '12px' }}></i> Xóa
                        </button>
                    </div>
                </div>
            )}

            <div
                className="table-card"
                ref={containerRef}
                onScroll={handleScroll}
                style={{
                    overflowX: 'auto',
                    paddingBottom: '10px',
                    position: 'relative'
                }}
            >
                {isLoading && (
                    <div style={{
                        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(255, 255, 255, 0.6)',
                        zIndex: 10, display: 'flex', justifyContent: 'center', paddingTop: '60px'
                    }}>
                        <i className="fa-solid fa-circle-notch fa-spin fa-2x" style={{ color: '#3498db', marginRight: '10px' }}></i>
                    </div>
                )}

                {data.length === 0 && !isLoading ? (
                    <div style={{ textAlign: 'center', padding: '60px 20px', color: '#666' }}>
                        <i className="fa-solid fa-file-invoice-dollar" style={{ fontSize: '60px', color: '#5dade2', marginBottom: '15px' }}></i>
                        <h3 style={{ color: '#e74c3c', margin: '0 0 10px 0' }}>Không tìm thấy dữ liệu xét thưởng</h3>
                    </div>
                ) : (
                    <>
                        <table className="custom-table" style={{ minWidth: '1000px' }}>
                            <thead>
                                <tr>
                                    <th width="3%" style={{ textAlign: 'center' }}>
                                        <input
                                            type="checkbox"
                                            style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                                            checked={data.length > 0 && selectedIds.length === data.length}
                                            onChange={handleSelectAll}
                                        />
                                    </th>
                                    <th width="3%" style={{ textAlign: 'center' }}>STT</th>
                                    <th width="18%">NGƯỜI ĐỀ XUẤT</th>
                                    <th width="12%" style={{ textAlign: 'center' }}>SỐ BÀI BÁO</th>
                                    <th width="12%" style={{ textAlign: 'center' }}>TỔNG TIỀN</th>
                                    <th width="24%">GHI CHÚ</th>
                                    <th width="10%" style={{ textAlign: 'center' }}>NGÀY TẠO</th>
                                    <th width="10%" style={{ textAlign: 'center' }}>TRẠNG THÁI</th>
                                    <th width="8%" style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>CHỨC NĂNG</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedData.map((item, index) => {
                                    const actualIndex = first + index + 1;

                                    return (
                                        <tr key={item.Id} style={{ backgroundColor: selectedIds.includes(item.Id) ? '#f4f9ff' : 'transparent' }}>
                                            <td style={{ textAlign: 'center' }}>
                                                <input
                                                    type="checkbox"
                                                    style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                                                    checked={selectedIds.includes(item.Id)}
                                                    onChange={(e) => handleSelectRow(e, item.Id)}
                                                />
                                            </td>
                                            <td style={{ textAlign: 'center' }}>{actualIndex}</td>
                                            <td>
                                                <div
                                                    style={{
                                                        fontWeight: '600',
                                                        color: item.Status === 'Đã duyệt' ? '#2980b9' : '#0056b3',
                                                        cursor: item.Status === 'Đã duyệt' ? 'pointer' : 'default',
                                                        textDecoration: item.Status === 'Đã duyệt' ? 'underline' : 'none',
                                                        transition: 'all 0.2s'
                                                    }}
                                                    onClick={(e) => {
                                                        if (item.Status === 'Đã duyệt') {
                                                            e.preventDefault();
                                                            onEdit(item);
                                                        }
                                                    }}
                                                    title={item.Status === 'Đã duyệt' ? "Nhấn để xem chi tiết xét thưởng" : ""}
                                                >
                                                    {item.FullName || item.RequesterName}
                                                </div>
                                                <div style={{ fontSize: '11px', color: '#777' }}>{item.Email}</div>
                                            </td>
                                            <td style={{ textAlign: 'center', fontWeight: 'bold', color: '#333' }}>{item.ArticleCount || 0} bài</td>
                                            <td style={{ textAlign: 'center', fontWeight: 'bold', color: '#e74c3c' }}>{formatCurrency(item.TotalAmount)}</td>
                                            <td>
                                                <div style={{ fontSize: '13px' }}>
                                                    {item.Notes || item.Note || '---'}
                                                    {item.Status === 'Từ chối' && item.AdminRequest && (
                                                        <div style={{ color: '#e74c3c', marginTop: '5px', fontSize: '12px', borderTop: '1px dashed #eee', paddingTop: '4px' }}>
                                                            <i className="fa-solid fa-circle-exclamation" style={{ marginRight: '5px' }}></i>
                                                            Lý do từ chối: {item.AdminRequest}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td style={{ textAlign: 'center', fontSize: '13px' }}>{item.CreatedDate || '---'}</td>
                                            <td style={{ textAlign: 'center' }}>
                                                <span style={{
                                                    ...getStatusStyle(item.Status),
                                                    padding: '4px 10px',
                                                    borderRadius: '12px',
                                                    fontSize: '11px',
                                                    fontWeight: 'bold',
                                                    whiteSpace: 'nowrap',
                                                    display: 'inline-block'
                                                }}>
                                                    {item.Status || "Lưu nháp"}
                                                </span>
                                            </td>
                                            <td style={{ textAlign: 'center' }}>
                                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                    <Button
                                                        icon="fa-solid fa-ellipsis-vertical"
                                                        className="p-button-rounded p-button-text p-button-plain"
                                                        style={{ color: '#6c757d', width: '2.5rem', height: '2.5rem', padding: 0 }}
                                                        onClick={(event) => {
                                                            setSelectedItem(item);
                                                            menu.current.toggle(event);
                                                        }}
                                                        aria-controls="popup_menu"
                                                        aria-haspopup
                                                    />
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>

                        {data.length > rows && (
                            <div style={{ marginTop: '15px', borderTop: '1px solid #e9ecef', paddingTop: '10px' }}>
                                <Paginator
                                    first={first}
                                    rows={rows}
                                    totalRecords={data.length}
                                    onPageChange={onPageChange}
                                    template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
                                    style={{ background: 'transparent', border: 'none' }}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>
            <Menu model={getMenuItems()} popup ref={menu} id="popup_menu_reward" appendTo={document.body} style={{ width: '220px', borderRadius: '8px', boxShadow: '0 4px 15px rgba(0,0,0,0.1)' }} />
        </>
    );
};

export default ArticleRewardListing;